<template>
  <div class="ajuda-variaveis">
    <div class="ajuda-variaveis-aviso">
      <v-icon small>mdi-code-braces</v-icon>
      <span class="ajuda-variaveis-aviso-texto">{{
        $t(`${definicao.namespace}.resumo`)
      }}</span>
      <v-btn
        small
        text
        class="ajuda-variaveis-botao text-none font-weight-bold"
        @click="abrir"
      >
        <v-icon small left>mdi-format-list-bulleted</v-icon>
        {{ $t("variaveisTexto.botao") }}
      </v-btn>
    </div>

    <v-dialog v-model="dialog" max-width="880" scrollable>
      <v-card class="ajuda-variaveis-card rounded-xl">
        <v-card-title class="ajuda-variaveis-cabecalho">
          <div>
            <div class="ajuda-variaveis-titulo">
              {{ $t("variaveisTexto.titulo") }}
            </div>
            <div class="ajuda-variaveis-subtitulo">
              {{ $t("variaveisTexto.instrucaoCopiar") }}
            </div>
          </div>
          <v-spacer />
          <v-btn
            icon
            :aria-label="$t('variaveisTexto.fechar')"
            @click="dialog = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <div class="ajuda-variaveis-busca">
          <v-text-field
            v-model="busca"
            :placeholder="$t('variaveisTexto.buscar')"
            prepend-inner-icon="mdi-magnify"
            class="
              input-style
              font-size-input
              text-light-input
              placeholder-light
              input-icon
            "
            hide-details
            clearable
            dense
            flat
            filled
            solo
          />
        </div>

        <v-card-text class="ajuda-variaveis-conteudo">
          <ul class="ajuda-variaveis-observacoes">
            <li v-for="observacao in definicao.observacoes" :key="observacao">
              {{ $t(`${definicao.namespace}.observacoes.${observacao}`) }}
            </li>
          </ul>

          <section
            v-for="grupo in gruposFiltrados"
            :key="grupo.chave"
            class="ajuda-variaveis-grupo"
          >
            <h4 class="ajuda-variaveis-grupo-titulo">{{ grupo.titulo }}</h4>
            <div
              v-for="variavel in grupo.variaveis"
              :key="variavel.nome"
              class="ajuda-variaveis-linha"
            >
              <button
                type="button"
                class="ajuda-variaveis-codigo"
                :title="$t('variaveisTexto.copiar')"
                @click="copiar(variavel.marcador)"
              >
                <span>{{ variavel.marcador }}</span>
                <v-icon x-small>{{
                  copiada === variavel.marcador
                    ? "mdi-check"
                    : "mdi-content-copy"
                }}</v-icon>
              </button>
              <span class="ajuda-variaveis-descricao">{{
                variavel.descricao
              }}</span>
            </div>
          </section>

          <div v-if="!gruposFiltrados.length" class="ajuda-variaveis-vazio">
            {{ $t("variaveisTexto.nenhuma") }}
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" color="#17804a" timeout="2000">
      {{ $t("variaveisTexto.copiada", { variavel: copiada }) }}
    </v-snackbar>
  </div>
</template>

<script>
function normalizar(texto) {
  return String(texto || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
}

export default {
  name: "AjudaVariaveisTexto",
  props: {
    // { namespace, observacoes: [chave], grupos: [{ chave, variaveis: [nome | { nome, chave, parametros }] }] }
    definicao: { type: Object, required: true },
  },
  data() {
    return {
      dialog: false,
      busca: "",
      copiada: "",
      snackbar: false,
    };
  },
  computed: {
    grupos() {
      const { namespace, grupos } = this.definicao;
      return grupos.map((grupo) => ({
        chave: grupo.chave,
        titulo: this.$t(`${namespace}.grupos.${grupo.chave}`),
        variaveis: grupo.variaveis.map((item) => {
          const variavel = typeof item === "string" ? { nome: item } : item;
          return {
            nome: variavel.nome,
            marcador: `@${variavel.nome}`,
            descricao: this.$t(
              `${namespace}.variaveis.${variavel.chave || variavel.nome}`,
              variavel.parametros
            ),
          };
        }),
      }));
    },
    gruposFiltrados() {
      const termo = normalizar(this.busca).trim();
      if (!termo) {
        return this.grupos;
      }
      return this.grupos
        .map((grupo) => {
          const grupoConfere = normalizar(grupo.titulo).includes(termo);
          return {
            ...grupo,
            variaveis: grupo.variaveis.filter(
              (variavel) =>
                grupoConfere ||
                normalizar(variavel.marcador).includes(termo) ||
                normalizar(variavel.descricao).includes(termo)
            ),
          };
        })
        .filter((grupo) => grupo.variaveis.length);
    },
  },
  methods: {
    abrir(evento) {
      // O v-dialog devolve o foco ao elemento ativo na abertura; sem isso ele volta ao último campo
      // do formulário e rola o modal do cadastro para o topo.
      if (evento && evento.currentTarget) {
        evento.currentTarget.focus({ preventScroll: true });
      }
      this.dialog = true;
    },
    async copiar(marcador) {
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(marcador);
        } else {
          const textarea = document.createElement("textarea");
          textarea.value = marcador;
          textarea.setAttribute("readonly", "");
          textarea.style.position = "absolute";
          textarea.style.left = "-9999px";
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand("copy");
          document.body.removeChild(textarea);
        }
        this.copiada = marcador;
        this.snackbar = true;
      } catch (error) {
        this.copiada = "";
      }
    },
  },
};
</script>

<style scoped>
.ajuda-variaveis-aviso {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 10px;
  align-items: center;
  margin-bottom: 8px;
  padding: 8px 12px;
  font-size: 0.84rem;
  line-height: 1.4;
  color: #1d3fa6;
  background: #eef4ff;
  border: 1px solid #c7d7fe;
  border-radius: 10px;
}

.ajuda-variaveis-aviso .v-icon {
  color: inherit !important;
}

.ajuda-variaveis-aviso-texto {
  flex: 1 1 260px;
}

.ajuda-variaveis-botao {
  color: #1d3fa6 !important;
}

.ajuda-variaveis-cabecalho {
  flex-wrap: nowrap;
  gap: 8px;
  align-items: flex-start;
  border-bottom: 1px solid #e9ecef;
}

.ajuda-variaveis-cabecalho > div {
  min-width: 0;
}

.ajuda-variaveis-titulo {
  font-size: 1.05rem;
  font-weight: 700;
  color: #344767;
}

.ajuda-variaveis-subtitulo {
  font-size: 0.82rem;
  font-weight: 400;
  color: #67748e;
  word-break: normal;
}

.ajuda-variaveis-busca {
  padding: 12px 24px 0;
}

.ajuda-variaveis-conteudo {
  padding-top: 12px !important;
  max-height: 65vh;
}

.ajuda-variaveis-observacoes {
  margin-bottom: 16px;
  padding-left: 18px;
  font-size: 0.82rem;
  color: #67748e;
}

.ajuda-variaveis-grupo + .ajuda-variaveis-grupo {
  margin-top: 16px;
}

.ajuda-variaveis-grupo-titulo {
  margin-bottom: 6px;
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #344767;
}

.ajuda-variaveis-linha {
  display: grid;
  grid-template-columns: minmax(0, 330px) minmax(0, 1fr);
  gap: 4px 14px;
  align-items: baseline;
  padding: 6px 0;
  border-bottom: 1px solid #f1f3f5;
}

.ajuda-variaveis-codigo {
  display: inline-flex;
  gap: 6px;
  align-items: center;
  justify-self: start;
  max-width: 100%;
  padding: 2px 8px;
  font-family: Menlo, Consolas, monospace;
  font-size: 0.78rem;
  color: #1d3fa6;
  text-align: left;
  word-break: break-all;
  background: #f4f6fb;
  border: 1px solid #dfe5f2;
  border-radius: 6px;
  cursor: pointer;
}

.ajuda-variaveis-codigo:hover,
.ajuda-variaveis-codigo:focus-visible {
  background: #e6edff;
  border-color: #b3c6fb;
}

.ajuda-variaveis-codigo .v-icon {
  color: inherit !important;
}

.ajuda-variaveis-descricao {
  font-size: 0.84rem;
  color: #344767;
}

.ajuda-variaveis-vazio {
  padding: 24px 0;
  text-align: center;
  color: #67748e;
}

@media (max-width: 600px) {
  .ajuda-variaveis-linha {
    grid-template-columns: minmax(0, 1fr);
  }

  .ajuda-variaveis-busca {
    padding: 12px 16px 0;
  }
}
</style>
